import type { z } from 'zod'

import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import {
  interactionInternalNoteDeleteSchema,
  interactionInternalNoteMutationSchema,
  interactionInternalNotesSchema,
} from './conversations-contracts'

export type InternalNote = z.infer<
  typeof interactionInternalNoteMutationSchema
>['note']
export type InternalNotesPage = z.infer<typeof interactionInternalNotesSchema>

export type InternalNotesRepository = {
  list: (
    tenantId: string,
    aggregateId: string,
    cursor?: string
  ) => Promise<InternalNotesPage>
  create: (
    tenantId: string,
    aggregateId: string,
    idempotencyKey: string,
    body: string
  ) => Promise<InternalNote>
  edit: (
    tenantId: string,
    aggregateId: string,
    noteRef: string,
    expectedRevision: number,
    idempotencyKey: string,
    body: string
  ) => Promise<InternalNote>
  remove: (
    tenantId: string,
    aggregateId: string,
    noteRef: string,
    expectedRevision: number,
    idempotencyKey: string
  ) => Promise<void>
}

export function createInternalNotesRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): InternalNotesRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  const path = (tenantId: string, aggregateId: string, noteRef?: string) =>
    `/dashboard/clinics/${encodeURIComponent(tenantId)}/interaction-aggregates/${encodeURIComponent(aggregateId)}/internal-notes${noteRef ? `/${encodeURIComponent(noteRef)}` : ''}`
  return {
    list(tenantId, aggregateId, cursor) {
      const query = new URLSearchParams({ pageSize: '20' })
      if (cursor) query.set('cursor', cursor)
      return client.request(`${path(tenantId, aggregateId)}?${query}`, {
        method: 'GET',
        cache: 'no-store',
        responseSchema: interactionInternalNotesSchema,
      })
    },
    async create(tenantId, aggregateId, idempotencyKey, body) {
      const result = await client.request(path(tenantId, aggregateId), {
        method: 'POST',
        body: { idempotencyKey, body },
        responseSchema: interactionInternalNoteMutationSchema,
      })
      return result.note
    },
    async edit(
      tenantId,
      aggregateId,
      noteRef,
      expectedRevision,
      idempotencyKey,
      body
    ) {
      const result = await client.request(
        path(tenantId, aggregateId, noteRef),
        {
          method: 'PATCH',
          body: { expectedRevision, idempotencyKey, body },
          responseSchema: interactionInternalNoteMutationSchema,
        }
      )
      return result.note
    },
    async remove(
      tenantId,
      aggregateId,
      noteRef,
      expectedRevision,
      idempotencyKey
    ) {
      await client.request(path(tenantId, aggregateId, noteRef), {
        method: 'DELETE',
        body: { expectedRevision, idempotencyKey },
        responseSchema: interactionInternalNoteDeleteSchema,
      })
    },
  }
}
